import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Table,
  Select,
  Button,
  Alert,
  Spin,
  Tag,
  Progress,
  Space,
  Typography,
  List,
  Empty,
  Tooltip
} from 'antd';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Legend
} from 'recharts';
import {
  DashboardOutlined,
  UserOutlined,
  CalendarOutlined,
  TrophyOutlined,
  ReloadOutlined,
  TeamOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  RiseOutlined
} from '@ant-design/icons';
import moment from 'moment';
import { toast } from 'react-toastify';
import { dashboardApi, campusApi } from '../../services/api';
import { usePermissions } from '../../hooks/usePermissions';

const { Option } = Select;
const { Title, Text } = Typography;

const Dashboard = () => {
  // Estados
  const [loading, setLoading] = useState(true);
  const [estadisticasGenerales, setEstadisticasGenerales] = useState(null);
  const [tendenciasCampus, setTendenciasCampus] = useState(null);
  const [indicadoresAusentismo, setIndicadoresAusentismo] = useState(null);
  const [rankingEmpleados, setRankingEmpleados] = useState(null);
  const [campusDisponibles, setCampusDisponibles] = useState([]);
  
  // Filtros
  const [diasTendencia, setDiasTendencia] = useState(30);
  const [periodoRanking, setPeriodoRanking] = useState('mes_actual');
  const [campusSeleccionado, setCampusSeleccionado] = useState(null); // null = todos los campus
  const [tipoVisualizacion, setTipoVisualizacion] = useState('auto'); // auto, lineas, barras
  const [periodoDistribucion, setPeriodoDistribucion] = useState('hoy'); // hoy, semana, mes, trimestre

  // Permisos
  const { canManageData, isReadOnly, roleName, isAdmin } = usePermissions();

  // Helper para extraer datos de la respuesta de la API
  const extractApiData = (response) => {
    console.log('extractApiData - response:', response);
    // Si la respuesta tiene la estructura { status: "success", data: {...} }
    if (response.data && response.data.status === 'success' && response.data.data) {
      return response.data.data;
    }
    // Si los datos están directamente en response.data
    return response.data;
  };

  // Cargar datos iniciales
  useEffect(() => {
    cargarDatosIniciales();
    cargarDistribucionPorTipo(periodoDistribucion); // Cargar distribución inicial
    if (isAdmin()) {
      cargarCampusDisponibles();
    }
  }, []);

  // Cargar distribución por tipo cuando cambia el período
  useEffect(() => {
    cargarDistribucionPorTipo(periodoDistribucion);
  }, [periodoDistribucion, campusSeleccionado]);

  // Recargar datos cuando cambia el campus seleccionado (solo para admin)
  useEffect(() => {
    if (isAdmin() && campusDisponibles.length > 0) {
      cargarDatosIniciales();
    }
  }, [campusSeleccionado]);

  const cargarCampusDisponibles = async () => {
    try {
      // Usar la API de campus para obtener todos los campus disponibles
      const response = await campusApi.getAll();
      console.log('Campus disponibles response:', response);
      
      if (response.data?.campus) {
        const campus = response.data.campus.map(c => ({
          id: c.id,
          nombre: c.campus
        }));
        setCampusDisponibles(campus);
      } else if (Array.isArray(response.data)) {
        const campus = response.data.map(c => ({
          id: c.id,
          nombre: c.campus
        }));
        setCampusDisponibles(campus);
      }
    } catch (error) {
      console.error('Error al cargar campus disponibles:', error);
      // Fallback: intentar obtener campus desde las tendencias
      try {
        const response = await dashboardApi.getTendenciasCampus(30);
        const data = extractApiData(response);
        
        if (data?.campus?.length > 0) {
          const campus = data.campus.map(c => ({
            id: c.campus_id,
            nombre: c.campus_nombre
          }));
          setCampusDisponibles(campus);
        }
      } catch (fallbackError) {
        console.error('Error en fallback para cargar campus:', fallbackError);
      }
    }
  };

  const cargarDatosIniciales = async () => {
    setLoading(true);
    try {
      await Promise.all([
        cargarEstadisticasGenerales(),
        canManageData() && cargarTendenciasCampus(),
        canManageData() && cargarIndicadoresAusentismo(),
        canManageData() && cargarRankingEmpleados()
      ].filter(Boolean));
    } catch (error) {
      console.error('Error al cargar datos del dashboard:', error);
      toast.error('Error al cargar los datos del dashboard');
    } finally {
      setLoading(false);
    }
  };

  const cargarEstadisticasGenerales = async () => {
    try {
      // Si es admin y tiene un campus seleccionado, usar ese campus
      const campusId = isAdmin() && campusSeleccionado ? campusSeleccionado : null;
      const response = await dashboardApi.getEstadisticasGenerales(campusId);
      console.log('Estadísticas generales response:', response);
      const data = extractApiData(response);
      console.log('Estadísticas generales data extracted:', data);
      setEstadisticasGenerales(data);
    } catch (error) {
      console.error('Error al cargar estadísticas generales:', error);
      if (error.response?.status === 403) {
        toast.error('No tienes permisos para acceder a los datos de este campus');
        setCampusSeleccionado(null); // Resetear selección
      } else {
        toast.error('Error al cargar estadísticas generales');
      }
    }
  };

  const cargarDistribucionPorTipo = async (periodo) => {
    try {
      // Si es admin y tiene un campus seleccionado, usar ese campus
      const campusId = isAdmin() && campusSeleccionado ? campusSeleccionado : null;
      
      // Mapear período a días
      const diasMap = {
        'hoy': 1,
        'semana': 7,
        'mes': 30,
        'trimestre': 90
      };
      
      const dias = diasMap[periodo] || 1;
      const limite = 10; // Mostrar top 10 tipos
      
      console.log('Cargando distribución por tipo:', { periodo, dias, limite, campusId });
      
      const response = await dashboardApi.getResumenNovedades(dias, limite, campusId);
      console.log('Resumen novedades response:', response);
      const data = extractApiData(response);
      console.log('Resumen novedades data extracted:', data);
      
      // Actualizar solo la parte de distribución por tipo
      if (data && data.resumen_por_tipo) {
        // Mapear la estructura del backend a la estructura esperada por el frontend
        const tiposNovedades = data.resumen_por_tipo.map(item => ({
          tipo: item.tipo_novedad,
          cantidad: item.total_ocurrencias
        }));

        setEstadisticasGenerales(prev => ({
          ...prev,
          novedades_por_tipo: tiposNovedades,
          periodo_distribucion: periodo,
          total_novedades_periodo: data.estadisticas_generales?.total_novedades || 0,
          periodo_info: data.periodo || {},
          estadisticas_detalladas: data.estadisticas_generales || {},
          resumen_detallado: data.resumen_por_tipo || [] // Guardar datos detallados
        }));
      }
    } catch (error) {
      console.error('Error al cargar distribución por tipo:', error);
      if (error.response?.status === 403) {
        toast.error('No tienes permisos para acceder a los datos de este campus');
        setCampusSeleccionado(null);
      } else {
        toast.error('Error al cargar la distribución por tipo');
      }
    }
  };

  const cargarTendenciasCampus = async () => {
    await cargarTendenciasCampusConDias(diasTendencia);
  };

  const cargarTendenciasCampusConDias = async (dias) => {
    try {
      // Si es admin y tiene un campus seleccionado, usar ese campus
      const campusId = isAdmin() && campusSeleccionado ? campusSeleccionado : null;
      const response = await dashboardApi.getTendenciasCampus(dias, campusId);
      console.log('Tendencias campus response:', response, 'con días:', dias);
      const data = extractApiData(response);
      console.log('Tendencias campus data extracted:', data);
      setTendenciasCampus(data);
    } catch (error) {
      console.error('Error al cargar tendencias de campus:', error);
      if (error.response?.status === 403) {
        toast.error('No tienes permisos para acceder a los datos de este campus');
        setCampusSeleccionado(null);
      }
    }
  };



  const cargarIndicadoresAusentismo = async () => {
    try {
      // Si es admin y tiene un campus seleccionado, usar ese campus
      const campusId = isAdmin() && campusSeleccionado ? campusSeleccionado : null;
      const response = await dashboardApi.getIndicadoresAusentismo('empleado', 10, 'desc', campusId);
      console.log('Indicadores ausentismo response:', response);
      const data = extractApiData(response);
      console.log('Indicadores ausentismo data extracted:', data);
      setIndicadoresAusentismo(data);
    } catch (error) {
      console.error('Error al cargar indicadores de ausentismo:', error);
      if (error.response?.status === 403) {
        toast.error('No tienes permisos para acceder a los datos de este campus');
        setCampusSeleccionado(null);
      }
    }
  };

  const cargarRankingEmpleados = async () => {
    await cargarRankingEmpleadosConPeriodo(periodoRanking);
  };

  const cargarRankingEmpleadosConPeriodo = async (periodo) => {
    try {
      // Si es admin y tiene un campus seleccionado, usar ese campus
      const campusId = isAdmin() && campusSeleccionado ? campusSeleccionado : null;
      const response = await dashboardApi.getRankingEmpleados(10, periodo, campusId);
      console.log('Ranking empleados response:', response, 'con periodo:', periodo);
      const data = extractApiData(response);
      console.log('Ranking empleados data extracted:', data);
      setRankingEmpleados(data);
    } catch (error) {
      console.error('Error al cargar ranking de empleados:', error);
      if (error.response?.status === 403) {
        toast.error('No tienes permisos para acceder a los datos de este campus');
        setCampusSeleccionado(null);
      }
    }
  };

  // Función para cargar comparativas (preparada para uso futuro)
  const cargarComparativas = async () => {
    try {
      const campusId = isAdmin() && campusSeleccionado ? campusSeleccionado : null;
      const response = await dashboardApi.getComparativas(new Date().getFullYear(), 'mensual', campusId);
      console.log('Comparativas response:', response);
      const data = extractApiData(response);
      console.log('Comparativas data extracted:', data);
      // setComparativas(data); // Descomentar cuando se implemente
    } catch (error) {
      console.error('Error al cargar comparativas:', error);
      if (error.response?.status === 403) {
        toast.error('No tienes permisos para acceder a los datos de este campus');
        setCampusSeleccionado(null);
      }
    }
  };

  // Componente mejorado para mostrar tendencias con gráfico completo
  const TendenciasChart = () => {
    console.log('TendenciasChart - tendenciasCampus:', tendenciasCampus);
    
    if (!tendenciasCampus?.campus?.length) {
      return <Empty description="No hay datos de tendencias disponibles" />;
    }

    // Si es admin y hay campus seleccionado, mostrar solo ese campus
    let campusAMostrar = tendenciasCampus.campus;
    
    if (isAdmin() && campusSeleccionado) {
      campusAMostrar = tendenciasCampus.campus.filter(c => c.campus_id === campusSeleccionado);
    }

    if (!campusAMostrar.length) {
      return <Empty description="No hay datos para el campus seleccionado" />;
    }

    // Preparar datos para el gráfico
    const prepararDatosGrafico = () => {
      if (campusAMostrar.length === 1) {
        // Un solo campus - gráfico de líneas
        const campusData = campusAMostrar[0];
        if (!campusData?.datos_diarios?.length) return [];

        return campusData.datos_diarios.map(item => ({
          fecha: moment(item.fecha).format('DD/MM'),
          fechaCompleta: moment(item.fecha).format('DD/MM/YYYY'),
          novedades: item.total_novedades || 0,
          campus: campusData.campus_nombre
        }));
      } else {
        // Múltiples campus - preparar datos para comparación
        const fechas = new Set();
        campusAMostrar.forEach(campus => {
          campus.datos_diarios?.forEach(dia => fechas.add(dia.fecha));
        });

        return Array.from(fechas).sort().map(fecha => {
          const dataPoint = {
            fecha: moment(fecha).format('DD/MM'),
            fechaCompleta: moment(fecha).format('DD/MM/YYYY')
          };

          campusAMostrar.forEach(campus => {
            const diaData = campus.datos_diarios?.find(d => d.fecha === fecha);
            dataPoint[campus.campus_nombre] = diaData?.total_novedades || 0;
          });

          return dataPoint;
        });
      }
    };

    const datosGrafico = prepararDatosGrafico();
    const totalNovedades = campusAMostrar.reduce((sum, campus) => sum + (campus.total_periodo || 0), 0);

    // Determinar si usar gráfico de líneas o barras según el tipo seleccionado
    const usarBarras = tipoVisualizacion === 'barras' || 
                      (tipoVisualizacion === 'auto' && diasTendencia <= 15);

    return (
      <div className="space-y-4">
        {/* Información del período */}
        <div className="flex justify-between items-center">
          <div>
            <Text strong>
              Período: {tendenciasCampus.periodo}
            </Text>
            <div className="flex gap-2 mt-1">
              {campusAMostrar.map((campus, index) => (
                <Tag key={index} color="blue">
                  {campus.campus_nombre}: {campus.total_periodo || 0} novedades
                </Tag>
              ))}
              <Tag color={usarBarras ? 'orange' : 'purple'}>
                {usarBarras ? 'Gráfico de Barras' : 'Gráfico de Líneas'}
              </Tag>
            </div>
          </div>
          <Tag color="green" className="text-base px-3 py-1">
            Total: {totalNovedades}
          </Tag>
        </div>

        {/* Gráfico */}
        <div style={{ width: '100%', height: 300 }}>
          <ResponsiveContainer>
            {usarBarras ? (
              <BarChart data={datosGrafico} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="fecha" 
                  tick={{ fontSize: 12 }}
                  angle={diasTendencia > 7 ? -45 : 0}
                  textAnchor={diasTendencia > 7 ? 'end' : 'middle'}
                  height={diasTendencia > 7 ? 60 : 30}
                />
                <YAxis tick={{ fontSize: 12 }} />
                <RechartsTooltip 
                  labelFormatter={(value, payload) => {
                    const item = datosGrafico.find(d => d.fecha === value);
                    return item ? `Fecha: ${item.fechaCompleta}` : value;
                  }}
                  formatter={(value, name) => [value, `${name}: ${value} novedades`]}
                />
                {campusAMostrar.length === 1 ? (
                  <Bar dataKey="novedades" fill="#1890ff" name={campusAMostrar[0].campus_nombre} />
                ) : (
                  campusAMostrar.map((campus, index) => (
                    <Bar 
                      key={campus.campus_id} 
                      dataKey={campus.campus_nombre} 
                      fill={['#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1'][index % 5]}
                    />
                  ))
                )}
                {campusAMostrar.length > 1 && <Legend />}
              </BarChart>
            ) : (
              <LineChart data={datosGrafico} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="fecha" 
                  tick={{ fontSize: 12 }}
                  angle={-45}
                  textAnchor="end"
                  height={60}
                />
                <YAxis tick={{ fontSize: 12 }} />
                <RechartsTooltip 
                  labelFormatter={(value, payload) => {
                    const item = datosGrafico.find(d => d.fecha === value);
                    return item ? `Fecha: ${item.fechaCompleta}` : value;
                  }}
                  formatter={(value, name) => [value, `${name}: ${value} novedades`]}
                />
                {campusAMostrar.length === 1 ? (
                  <Line 
                    type="monotone" 
                    dataKey="novedades" 
                    stroke="#1890ff" 
                    strokeWidth={2}
                    dot={{ fill: '#1890ff', strokeWidth: 2, r: 4 }}
                    name={campusAMostrar[0].campus_nombre}
                  />
                ) : (
                  campusAMostrar.map((campus, index) => (
                    <Line 
                      key={campus.campus_id}
                      type="monotone" 
                      dataKey={campus.campus_nombre} 
                      stroke={['#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1'][index % 5]}
                      strokeWidth={2}
                      dot={{ strokeWidth: 2, r: 4 }}
                    />
                  ))
                )}
                {campusAMostrar.length > 1 && <Legend />}
              </LineChart>
            )}
          </ResponsiveContainer>
        </div>

        {/* Estadísticas adicionales */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
          {campusAMostrar.map((campus, index) => {
            const datos = campus.datos_diarios || [];
            const promedio = datos.length > 0 ? (campus.total_periodo || 0) / datos.length : 0;
            const maximo = Math.max(...datos.map(d => d.total_novedades || 0));
            
            return (
              <Card key={index} size="small">
                <Statistic
                  title={campus.campus_nombre}
                  value={promedio.toFixed(1)}
                  suffix="prom/día"
                  precision={1}
                  valueStyle={{ fontSize: '16px' }}
                />
                <Text type="secondary" className="text-xs">
                  Máximo: {maximo} | Total: {campus.total_periodo || 0}
                </Text>
              </Card>
            );
          })}
        </div>
      </div>
    );
  };

  // Componente para distribución por tipo
  const DistribucionTipos = () => {
    console.log('DistribucionTipos - estadisticasGenerales:', estadisticasGenerales);
    
    if (!estadisticasGenerales?.novedades_por_tipo?.length) {
      return <Empty description="No hay datos de tipos de novedades para este período" />;
    }

    const total = estadisticasGenerales.total_novedades_periodo || 
                  estadisticasGenerales.novedades_por_tipo.reduce((sum, item) => sum + (item.cantidad || 0), 0);
    
    // Mapear período a texto descriptivo
    const periodoTexto = {
      'hoy': 'Hoy',
      'semana': 'Últimos 7 días',
      'mes': 'Últimos 30 días',
      'trimestre': 'Últimos 90 días'
    };

    return (
      <div className="space-y-3">
        {/* Información del período */}
        <div className="flex justify-between items-center mb-3">
          <div>
            <Text type="secondary" className="text-xs">
              {estadisticasGenerales.periodo_info?.descripcion || periodoTexto[periodoDistribucion] || 'Hoy'}
            </Text>
            {estadisticasGenerales.periodo_info && (
              <div className="text-xs text-gray-400">
                {estadisticasGenerales.periodo_info.fecha_inicio && estadisticasGenerales.periodo_info.fecha_fin && (
                  `${moment(estadisticasGenerales.periodo_info.fecha_inicio).format('DD/MM')} - ${moment(estadisticasGenerales.periodo_info.fecha_fin).format('DD/MM')}`
                )}
              </div>
            )}
          </div>
          <div className="flex flex-col items-end gap-1">
            <Tag color="blue" size="small">
              Total: {total}
            </Tag>
            {estadisticasGenerales.estadisticas_detalladas?.total_empleados_afectados && (
              <Tag color="green" size="small">
                {estadisticasGenerales.estadisticas_detalladas.total_empleados_afectados} empleados
              </Tag>
            )}
          </div>
        </div>
        
        {estadisticasGenerales.novedades_por_tipo.map((item, index) => {
          const percentage = total > 0 ? (((item.cantidad || 0) / total) * 100).toFixed(1) : 0;
          const colors = ['#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1', '#fa8c16', '#13c2c2', '#eb2f96'];
          
          // Buscar información adicional del resumen_por_tipo original
          const detalleItem = estadisticasGenerales.resumen_detallado?.find(detalle => 
            detalle.tipo_novedad === item.tipo
          );
          
          return (
            <div key={index}>
              <div className="flex justify-between items-center mb-1">
                <Tooltip 
                  title={
                    <div>
                      <div><strong>{item.tipo}</strong></div>
                      {detalleItem && (
                        <>
                          <div>Empleados afectados: {detalleItem.empleados_afectados}</div>
                          <div>Promedio diario: {detalleItem.promedio_diario}</div>
                          <div>Días con ocurrencias: {detalleItem.dias_con_ocurrencias}</div>
                        </>
                      )}
                    </div>
                  }
                >
                  <span className="text-sm text-gray-600 truncate cursor-help" style={{ maxWidth: '140px' }}>
                    {item.tipo && item.tipo.length > 16 ? `${item.tipo.substring(0, 16)}...` : item.tipo}
                  </span>
                </Tooltip>
                <span className="text-sm font-medium">{item.cantidad || 0} ({percentage}%)</span>
              </div>
              <Progress
                percent={parseFloat(percentage)}
                showInfo={false}
                strokeColor={colors[index % colors.length]}
              />
            </div>
          );
        })}
        
        {/* Información adicional */}
        <div className="mt-4 pt-3 border-t border-gray-100">
          <div className="grid grid-cols-2 gap-2 text-xs">
            {estadisticasGenerales.estadisticas_detalladas?.promedio_novedades_diario && (
              <div className="text-center">
                <Text type="secondary">Promedio diario</Text>
                <div className="font-medium">{estadisticasGenerales.estadisticas_detalladas.promedio_novedades_diario}</div>
              </div>
            )}
            {estadisticasGenerales.estadisticas_detalladas?.tipos_novedad_diferentes && (
              <div className="text-center">
                <Text type="secondary">Tipos diferentes</Text>
                <div className="font-medium">{estadisticasGenerales.estadisticas_detalladas.tipos_novedad_diferentes}</div>
              </div>
            )}
          </div>
          
          {estadisticasGenerales.novedades_por_tipo.length > 0 && (
            <Text type="secondary" className="text-xs text-center block mt-2">
              Mostrando {estadisticasGenerales.novedades_por_tipo.length} tipos de novedades
            </Text>
          )}
        </div>
      </div>
    );
  };

  // Columnas para tablas
  const columnasRanking = [
    {
      title: 'Pos.',
      dataIndex: 'posicion',
      key: 'posicion',
      width: 60,
      align: 'center',
      render: (pos) => (
        <Tag color={pos <= 3 ? 'red' : pos <= 5 ? 'orange' : 'default'}>
          {pos}
        </Tag>
      )
    },
    {
      title: 'Empleado',
      dataIndex: 'empleado_nombre',
      key: 'empleado_nombre',
      ellipsis: true,
    },
    {
      title: 'Cédula',
      dataIndex: 'cedula',
      key: 'cedula',
      width: 120,
    },
    {
      title: 'Total',
      dataIndex: 'total_novedades',
      key: 'total_novedades',
      width: 80,
      align: 'center',
      render: (total) => (
        <Tag color="blue">{total}</Tag>
      )
    }
  ];

  const columnasAusentismo = [
    {
      title: 'Empleado',
      dataIndex: 'empleado_nombre',
      key: 'empleado_nombre',
      ellipsis: true,
    },
    {
      title: 'Ausentismo',
      dataIndex: 'porcentaje_ausentismo',
      key: 'porcentaje_ausentismo',
      width: 120,
      align: 'center',
      render: (porcentaje) => (
        <Progress
          percent={porcentaje}
          size="small"
          status={porcentaje > 15 ? 'exception' : porcentaje > 10 ? 'active' : 'success'}
          format={(percent) => `${percent}%`}
        />
      )
    },
    {
      title: 'Novedades',
      dataIndex: 'total_novedades',
      key: 'total_novedades',
      width: 100,
      align: 'center',
    }
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <Spin size="large" />
      </div>
    );
  }

  // Debug logs
  console.log('Dashboard render - estadisticasGenerales:', estadisticasGenerales);
  console.log('Dashboard render - tendenciasCampus:', tendenciasCampus);
  console.log('Dashboard render - canManageData:', canManageData());

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex justify-between items-start">
          <div>
            <Title level={2} className="flex items-center mb-2">
              <DashboardOutlined className="mr-3" />
              Dashboard Analítico
            </Title>
            <Text type="secondary">
              Panel de control y análisis de novedades de personal - {roleName}
            </Text>
          </div>
          
          {/* Selector de Campus - Solo para Administradores */}
          {isAdmin() && campusDisponibles.length > 0 && (
            <div className="flex items-center gap-3">
              <Text strong>Filtrar por Campus:</Text>
              <Select
                placeholder="Todos los campus"
                allowClear
                style={{ width: 200 }}
                value={campusSeleccionado}
                onChange={(value) => {
                  setCampusSeleccionado(value);
                }}
                options={[
                  { value: null, label: 'Todos los campus' },
                  ...campusDisponibles.map(campus => ({
                    value: campus.id,
                    label: campus.nombre
                  }))
                ]}
              />
            </div>
          )}
        </div>
      </div>

      {/* Alerta para usuarios con permisos limitados */}
      {isReadOnly() && (
        <Alert
          message="Vista de Solo Lectura"
          description="Como auditor, tienes acceso limitado a las estadísticas generales del dashboard."
          type="info"
          showIcon
          className="mb-6"
        />
      )}

      {/* Información del campus seleccionado */}
      {isAdmin() && campusSeleccionado && (
        <Alert
          message={`Vista filtrada por Campus: ${campusDisponibles.find(c => c.id === campusSeleccionado)?.nombre || 'Desconocido'}`}
          description="Todos los datos (estadísticas, tendencias, rankings e indicadores) están filtrados para este campus específico."
          type="info"
          showIcon
          className="mb-6"
          action={
            <Button 
              size="small" 
              type="text" 
              onClick={() => setCampusSeleccionado(null)}
            >
              Ver todos
            </Button>
          }
        />
      )}

      {/* Estadísticas Generales */}
      <Row gutter={[16, 16]} className="mb-6">
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Novedades Hoy"
              value={estadisticasGenerales?.novedades_hoy || 0}
              prefix={<CalendarOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Empleados Activos"
              value={estadisticasGenerales?.total_empleados_activos || 0}
              prefix={<TeamOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Ausentismo Hoy"
              value={estadisticasGenerales?.porcentaje_ausentismo_hoy || 0}
              suffix="%"
              prefix={<ExclamationCircleOutlined />}
              valueStyle={{ 
                color: (estadisticasGenerales?.porcentaje_ausentismo_hoy || 0) > 10 ? '#ff4d4f' : '#52c41a' 
              }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Campus"
              value={
                isAdmin() && campusSeleccionado 
                  ? campusDisponibles.find(c => c.id === campusSeleccionado)?.nombre || 'N/A'
                  : estadisticasGenerales?.campus_usuario || (isAdmin() ? 'Todos' : 'N/A')
              }
              prefix={<UserOutlined />}
              valueStyle={{
                color: isAdmin() && campusSeleccionado ? '#1890ff' : '#52c41a'
              }}
            />
            <Text type="secondary" className="text-xs">
              {isAdmin() && campusSeleccionado 
                ? 'Vista filtrada' 
                : isAdmin() 
                  ? 'Vista global' 
                  : 'Campus asignado'
              }
            </Text>
          </Card>
        </Col>
      </Row>

      {/* Gráficos y Análisis - Solo para usuarios con permisos */}
      {canManageData() && (
        <>
          {/* Tendencias y Distribución */}
          <Row gutter={[16, 16]} className="mb-6">
            <Col xs={24} lg={16}>
              <Card
                title={
                  <div className="flex justify-between items-center">
                    <span>
                      <RiseOutlined className="mr-2" />
                      Tendencia de Novedades
                    </span>
                    <Space>
                      <Select
                        value={diasTendencia}
                        onChange={(value) => {
                          setDiasTendencia(value);
                          cargarTendenciasCampusConDias(value);
                        }}
                        style={{ width: 120 }}
                      >
                        <Option value={7}>7 días</Option>
                        <Option value={15}>15 días</Option>
                        <Option value={30}>30 días</Option>
                        <Option value={90}>90 días</Option>
                      </Select>
                      <Select
                        value={tipoVisualizacion}
                        onChange={setTipoVisualizacion}
                        style={{ width: 100 }}
                        size="small"
                      >
                        <Option value="auto">Auto</Option>
                        <Option value="lineas">Líneas</Option>
                        <Option value="barras">Barras</Option>
                      </Select>
                      <Button
                        icon={<ReloadOutlined />}
                        onClick={cargarTendenciasCampus}
                        size="small"
                      />
                    </Space>
                  </div>
                }
              >
                <TendenciasChart />
              </Card>
            </Col>
            <Col xs={24} lg={8}>
              <Card 
                title={
                  <div className="flex justify-between items-center">
                    <span>Distribución por Tipo</span>
                    <Space>
                      <Select
                        value={periodoDistribucion}
                        onChange={(value) => {
                          setPeriodoDistribucion(value);
                          cargarDistribucionPorTipo(value);
                        }}
                        style={{ width: 120 }}
                        size="small"
                      >
                        <Option value="hoy">Hoy</Option>
                        <Option value="semana">7 días</Option>
                        <Option value="mes">30 días</Option>
                        <Option value="trimestre">90 días</Option>
                      </Select>
                      <Button
                        icon={<ReloadOutlined />}
                        onClick={() => cargarDistribucionPorTipo(periodoDistribucion)}
                        size="small"
                        type="text"
                      />
                    </Space>
                  </div>
                }
              >
                <DistribucionTipos />
              </Card>
            </Col>
          </Row>

          {/* Rankings y Análisis */}
          <Row gutter={[16, 16]} className="mb-6">
            <Col xs={24} lg={12}>
              <Card
                title={
                  <div className="flex justify-between items-center">
                    <span>
                      <TrophyOutlined className="mr-2" />
                      Ranking de Empleados
                    </span>
                    <Select
                      value={periodoRanking}
                      onChange={(value) => {
                        setPeriodoRanking(value);
                        // Llamar cargarRankingEmpleados con el nuevo valor directamente
                        cargarRankingEmpleadosConPeriodo(value);
                      }}
                      style={{ width: 150 }}
                    >
                      <Option value="mes_actual">Mes Actual</Option>
                      <Option value="mes_anterior">Mes Anterior</Option>
                      <Option value="trimestre_actual">Trimestre</Option>
                      <Option value="ultimos_30_dias">Últimos 30 días</Option>
                      <Option value="ultimos_7_dias">Últimos 7 días</Option>
                    </Select>
                  </div>
                }
              >
                <Table
                  dataSource={rankingEmpleados?.ranking || []}
                  columns={columnasRanking}
                  pagination={false}
                  size="small"
                  scroll={{ y: 300 }}
                  rowKey="empleado_id"
                />
              </Card>
            </Col>
            <Col xs={24} lg={12}>
              <Card
                title={
                  <span>
                    <ClockCircleOutlined className="mr-2" />
                    Indicadores de Ausentismo
                  </span>
                }
              >
                <Table
                  dataSource={indicadoresAusentismo?.indicadores || []}
                  columns={columnasAusentismo}
                  pagination={false}
                  size="small"
                  scroll={{ y: 300 }}
                  rowKey="empleado_id"
                />
              </Card>
            </Col>
          </Row>
        </>
      )}

      {/* Información adicional */}
      <Row gutter={[16, 16]} className="mb-6">
        <Col xs={24}>
          <Card title="Resumen de Tipos de Novedades">
            {estadisticasGenerales?.novedades_por_tipo?.length > 0 ? (
              <List
                grid={{ gutter: 16, xs: 1, sm: 2, md: 3, lg: 4, xl: 6 }}
                dataSource={estadisticasGenerales.novedades_por_tipo}
                renderItem={(item, index) => (
                  <List.Item>
                    <Card size="small">
                      <Statistic
                        title={item.tipo}
                        value={item.cantidad}
                        valueStyle={{ 
                          color: ['#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1'][index % 5] 
                        }}
                      />
                    </Card>
                  </List.Item>
                )}
              />
            ) : (
              <Empty description="No hay datos de tipos de novedades" />
            )}
          </Card>
        </Col>
      </Row>

      {/* Botón de actualización */}
      <div className="text-center">
        <Space>
          <Button
            type="primary"
            icon={<ReloadOutlined />}
            onClick={cargarDatosIniciales}
            loading={loading}
            size="large"
          >
            Actualizar Dashboard
          </Button>
          
          {isAdmin() && campusSeleccionado && (
            <Button
              icon={<ReloadOutlined />}
              onClick={() => {
                setCampusSeleccionado(null);
                toast.info('Mostrando datos de todos los campus');
              }}
              size="large"
            >
              Ver Todos los Campus
            </Button>
          )}
        </Space>
      </div>
    </div>
  );
};

export default Dashboard;